/**
 * Formatadores para exportação.
 *
 * Cada formato consumidor (PDF, Excel, CSV) chama um destes para converter
 * o valor cru em algo apresentável.
 *
 *  - displayCell(value, type) → string formatada (usada em PDF e CSV).
 *  - rawCell(value, type)     → valor cru tipado para Excel/sheet (Date,
 *                               number etc. — o xlsx serializa correto).
 *  - excelFormat(type)        → string z= do xlsx para formatar a célula.
 */

import { formatarData, formatarMoeda, parseNumero } from "../calculos";
import { toDate } from "../status";
import { hasValue } from "../data";

const FORMATO_DATA_HORA = {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
};

// Carimbo com fuso ("2026-09-30T12:27:00Z") -> Date no fuso do navegador.
// NAO usar toDate: ele le so o AAAA-MM-DD do texto e perde a hora (e o dia,
// quando o horario em UTC ja virou o dia seguinte).
function dataHora(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const numberFormatter = new Intl.NumberFormat("pt-BR");
const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function displayCell(value, type = "text") {
  if (!hasValue(value)) return "";
  switch (type) {
    case "currency":
      return formatarMoeda(value);
    case "date": {
      const d = toDate(value);
      return d ? formatarData(d) : "";
    }
    case "datetime": {
      const d = dataHora(value);
      return d ? d.toLocaleString("pt-BR", FORMATO_DATA_HORA).replace(",", "") : "";
    }
    case "days": {
      const n = parseNumero(value);
      return n == null ? "" : `${Math.round(n)} du`;
    }
    case "percentage": {
      const n = parseNumero(value);
      return n == null ? "" : `${percentFormatter.format(n)}%`;
    }
    case "number": {
      const n = parseNumero(value);
      return n == null ? "" : numberFormatter.format(n);
    }
    case "status":
    case "text":
    default:
      return String(value);
  }
}

export function rawCell(value, type = "text") {
  if (!hasValue(value)) return "";
  switch (type) {
    case "currency":
    case "number":
    case "days":
    case "percentage": {
      const n = parseNumero(value);
      return n == null ? "" : n;
    }
    case "date": {
      const d = toDate(value);
      return d || "";
    }
    case "datetime":
      return dataHora(value) || "";
    case "status":
    case "text":
    default:
      return String(value);
  }
}

// Retorna mascara z= compatível com xlsx para formatar a célula no Excel.
export function excelFormat(type) {
  switch (type) {
    case "currency":
      return 'R$ #,##0.00;[Red]-R$ #,##0.00';
    case "date":
      return "dd/mm/yyyy";
    case "datetime":
      return "dd/mm/yyyy hh:mm";
    case "percentage":
      return "0.0%";
    case "number":
      return "#,##0";
    case "days":
      return '#,##0" du"';
    default:
      return null; // texto cru
  }
}

// Slug para nome de arquivo seguro em filesystem.
export function slugifyFilename(input) {
  return String(input || "Exportação")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "Exportação";
}

export function timestampForFilename(now = new Date()) {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}_${hh}${mi}`;
}
